// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import FolderOffOutlinedIcon from '@mui/icons-material/FolderOffOutlined';
import { PROJECTS_ROUTE } from 'Utils/routes';
import stylesDef from './styles';

export const FOLDER_UNAVAILABLE_COPY = {
  notFound: {
    title: 'This folder no longer exists.',
    detail: 'It may have been deleted. Projects that were in it are listed on All Projects.',
  },
  unreadable: {
    title: 'This folder could not be loaded.',
    detail: 'Try again, or go back to All Projects.',
  },
};

interface IFolderUnavailableProps {
  reason: keyof typeof FOLDER_UNAVAILABLE_COPY;
  // Offered when the folder could not be read: read it again.
  onTryAgain: (() => void) | undefined;
}

// What a folder's page shows in place of the folder when the folder is gone, or cannot be read: why, and the way back
// to All Projects (and Try again when the read failed). Nothing on it can file a project.
export const FolderUnavailable = ({ reason, onTryAgain }: IFolderUnavailableProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const { title, detail } = FOLDER_UNAVAILABLE_COPY[reason];

  return (
    <Box sx={styles.container}>
      <FolderOffOutlinedIcon sx={styles.icon} />
      <Typography variant="h3">{title}</Typography>
      <Typography variant="body1">{detail}</Typography>
      <Box sx={styles.actions}>
        {onTryAgain && (
          <Button variant="outlined" color="primary" onClick={onTryAgain}>
            Try again
          </Button>
        )}
        <Button variant="contained" color="primary" component={RouterLink} to={PROJECTS_ROUTE}>
          Back to All Projects
        </Button>
      </Box>
    </Box>
  );
};

export default FolderUnavailable;
